import { Button } from "components/DesignSystem/Button/Button";
import Modal from "components/DesignSystem/Modal/Modal";
import { TextArea } from "components/DesignSystem/TextArea/TextArea";
import { FormikForm } from "components/FormikForm/FormikForm";
import { ThumbsUp } from "components/icons/ThumbsUp";
import { FormikValues } from "formik";
import { useModal } from "hooks/useModal";
import { useToast } from "hooks/useToast";
import React, { useState } from "react";
import { BackendError } from "types/utils/error";
import { object, string } from "yup";
import { MessageComponentProps } from "./MessageBubble";
import { useGiveAIResponseFeedbackMutation } from "store/apis/chat";
import { useChatContext } from "stream-chat-react";
import { LoadingIcon } from "components/icons/LoadingIcon";

const FeedbackWrapper = ({ children }: { children: React.ReactNode }) => (
  <div className="t-ml-10 t-mt-1">{children}</div>
);

const ThankYouMessage = () => (
  <div className="t-text-body-sm t-text-text-30">
    Thank you for the feedback!
  </div>
);

const AdditionalFeedbackButton = ({
  onClick,
  message,
  channelId,
  onClose,
  isModalOpen,
}: {
  onClick: () => void;
  message: MessageComponentProps["message"];
  channelId: string;
  onClose: () => void;
  isModalOpen: boolean;
}) => (
  <>
    <Button customType="ghost" size="xsmall" onClick={onClick}>
      <span className="t-text-subtext-sm t-text-text-30">
        Give Additional Feedback
      </span>
    </Button>
    {isModalOpen && (
      <AdditionalFeedback
        message={message}
        channelId={channelId}
        onClose={onClose}
      />
    )}
  </>
);

const ThumbsButtons = ({
  isLoading,
  onThumbsAction,
  message,
  channelId,
  onClose,
  isModalOpen,
}: {
  isLoading: boolean;
  onThumbsAction: (rating: 1 | -1) => void;
  message: MessageComponentProps["message"];
  channelId: string;
  onClose: () => void;
  isModalOpen: boolean;
}) => (
  <>
    <div className="t-flex t-gap-1.5 t-items-center">
      {isLoading ? (
        <span className="t-flex t-origin-center t-animate-spin">
          <LoadingIcon />
        </span>
      ) : (
        <>
          <Button
            customType="ghost_icon"
            size="xsmall"
            onClick={() => onThumbsAction(1)}
          >
            <span className="t-text-text-30 -t-mt-1">
              <ThumbsUp />
            </span>
          </Button>
          <Button
            customType="ghost_icon"
            size="xsmall"
            onClick={() => onThumbsAction(-1)}
          >
            <span className="t-text-text-30 t-rotate-180">
              <ThumbsUp />
            </span>
          </Button>
        </>
      )}
    </div>
    {isModalOpen && (
      <AdditionalFeedback
        message={message}
        channelId={channelId}
        onClose={onClose}
      />
    )}
  </>
);

export const AIResponseFeedback = ({
  message,
  channelId,
}: {
  message: MessageComponentProps["message"];
  channelId: string;
}) => {
  const aiResponseFeedback = useModal();
  const { client } = useChatContext();

  const feedbackGiven = Boolean(
    message.custom_data?.ai_agent_validation?.thumbs_rating
  );
  const additionalFeedbackGiven = Boolean(
    message.custom_data?.ai_agent_validation?.user_feedback
  );
  const { alertToast, successToast } = useToast();
  const [giveAiResponse, { isLoading }] = useGiveAIResponseFeedbackMutation();

  const handleThumbsAction = async (thumbsRating: 1 | -1) => {
    try {
      await giveAiResponse({
        channelId,
        messageId: message.id,
        body: { thumbs_rating: thumbsRating },
      }).unwrap();
      successToast({
        title: "Feedback recorded",
        message: "Submitted to the system",
      });
      aiResponseFeedback.open();
    } catch (error) {
      alertToast({ message: (error as BackendError)?.data?.error?.message });
    }
  };

  const renderFeedbackContent = () => {
    if (additionalFeedbackGiven) {
      return <ThankYouMessage />;
    }

    if (feedbackGiven) {
      return (
        <AdditionalFeedbackButton
          onClick={aiResponseFeedback.open}
          message={message}
          channelId={channelId}
          onClose={aiResponseFeedback.close}
          isModalOpen={aiResponseFeedback.isOpen}
        />
      );
    }

    return (
      <ThumbsButtons
        isLoading={isLoading}
        onThumbsAction={handleThumbsAction}
        message={message}
        channelId={channelId}
        onClose={aiResponseFeedback.close}
        isModalOpen={aiResponseFeedback.isOpen}
      />
    );
  };

  return <FeedbackWrapper>{renderFeedbackContent()}</FeedbackWrapper>;
};

export const AdditionalFeedback = ({
  message,
  channelId,
  onClose,
}: {
  message: MessageComponentProps["message"];
  onClose: () => void;
  channelId: string;
}) => {
  const { alertToast, successToast } = useToast();
  const [giveAiResponse, { isLoading }] = useGiveAIResponseFeedbackMutation();

  const handleSubmit = async (values: FormikValues) => {
    try {
      await giveAiResponse({
        channelId,
        messageId: message.id,
        body: { user_feedback: values.response },
      }).unwrap();
      successToast({
        title: "Feedback recorded",
        message: "Submitted to the system",
      });
      onClose();
    } catch (error) {
      alertToast({ message: (error as BackendError)?.data?.error?.message });
    }
  };

  return (
    <Modal.Root open onOpenChange={onClose}>
      <Modal.Content size="small">
        <FormikForm
          initialValues={{ response: "" }}
          onSubmit={handleSubmit}
          validationSchema={object({
            response: string().required("Response is required"),
          })}
        >
          <>
            <Modal.Header>
              <Modal.Title>Additional feedback</Modal.Title>
              <Modal.Close />
            </Modal.Header>
            <Modal.Body>
              <TextArea
                name="response"
                label="How were the responses from the AI?"
                placeholder="Please provide any additional feedback here..."
                required
              />
            </Modal.Body>
            <Modal.FooterButtonGroup>
              <Modal.RawClose asChild>
                <Button>Cancel</Button>
              </Modal.RawClose>
              <Button type="submit" customType="primary" isLoading={isLoading}>
                Save
              </Button>
            </Modal.FooterButtonGroup>
          </>
        </FormikForm>
      </Modal.Content>
    </Modal.Root>
  );
};
